import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { AppService } from './app.service';
import { UtilsService } from './shared/utils.service';

import {
  BreakpointObserver,
  Breakpoints,
  MediaMatcher
} from '@angular/cdk/layout';
import { setTheme } from 'ngx-bootstrap/utils';
import { Observable, Subscription } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { RouterService } from './shared/router.service';
import { UserMessagesService } from './user/user-messages/user-messages.service';
import {GoogleAnalyticsServiceService} from "./services/google-analytics-service.service";
import { AppcuesService } from './services/appcues.service';
import { HubspotService } from './services/hubspot.service';
import {SentryService} from "./services/sentry.service";
import { RBACService } from './shared/rbac.service';
import { AuthService } from './shared/auth.service';
import { StorageService } from './shared/storage.service';
import { ApiRequestService } from './shared/api-request.service';
import { AccessRequestsService } from './employees/access-requests/access-requests.service';
import { UserAccessRequestsService } from './user/account-access/user-access-requests.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('sidenav') sidenav: MatSidenav;
  mobileQuery: MediaQueryList;
  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(map((result) => result.matches));

  menu: any;
  private readonly _mobileQueryListener: () => void;

  minuteInterval: any;
  // How long to wait after the network or the tab comes back before the first request, so it does not
  // straddle the network change.
  private static readonly RESUME_DELAY_MS: number = 2000;
  // True when a scheduled poll was skipped (offline, hidden or settling) and is owed after resume.
  private pollSkipped: boolean = false;
  // Polls are skipped until this timestamp, the settle window after a resume.
  private pollNotBefore: number = 0;
  private pollResumeTimer?: ReturnType<typeof setTimeout>;

  // Default the primary color to the SiteConnect color.
  primaryColor: string = '#F4901E';

  /**
   * Left navigation visibility flags, each combining the subscription plan gate with the RBAC check
   * for that menu entry.
   *
   * These are resolved once per permission change instead of being evaluated inline in the template.
   * The navigation is always rendered, so inline calls ran on every change detection cycle (hundreds
   * of permission array scans per cycle). This component is never destroyed, so the flags are
   * recomputed from rbac.permissionsChanged$ (sign in, sign out, account switch, quick access).
   */
  navCan = {
    sites: false,
    safetyPlans: false,
    contractors: false,
    employees: false,
    tasks: false,
    accountInductions: false,
    forms: false,
    registers: false,
    registersIncidents: false,
    registersSafetyObservations: false,
    registersHazards: false,
    registersHazardousSubstances: false,
    registersInspectionsAudits: false,
    registersToolboxTalks: false,
    registersTools: false,
    registersAssets: false,
    registersAssetTypes: false,
    registersVendors: false,
    templates: false,
    templatesTaskAnalysis: false,
    templatesTaJsaSwms: false,
    templatesDocuments: false,
    templatesPreQualifications: false,
    templatesInspectionsAudits: false,
    templatesToolboxTalks: false,
    reports: false,
    reportsSiteAccess: false,
    reportsVisitors: false,
    messages: false,
    clientSites: false,
    clientPreQualifications: false
  };

  /**
   * Pending membership requests awaiting this account's approval (SC-301), for the Employees menu badge.
   */
  pendingAccessRequests: number = 0;

  /**
   * The requesting user's own pending request, for the shell banner (SC-301).
   *
   * Somebody whose request is pending is signed in but cannot see the account they asked to join, which
   * without an explanation looks exactly like the product being broken. The banner is the explanation, and
   * it lives in the shell so it follows them onto whatever page they land on.
   */
  ownPendingRequest: { id: number; account_name: string } = null;

  /**
   * How many requests are pending. A user can be waiting on several companies at once, so the banner names
   * the company only when there is exactly one to name.
   */
  ownPendingCount: number = 0;

  private permissionsSubscription?: Subscription;
  private pendingCountSubscription?: Subscription;
  private ownRequestsSubscription?: Subscription;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    public routerService: RouterService,
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private userMessagesService: UserMessagesService,
    private googleAnalyticsService: GoogleAnalyticsServiceService, // Load this for Google Analytics tracking even if it's not used here.
    private appcuesService: AppcuesService,
    private hubspotService: HubspotService,
    private sentry: SentryService,
    public rbac: RBACService,
    private authService: AuthService,
    private storage: StorageService,
    private api: ApiRequestService,
    private accessRequestsService: AccessRequestsService,
    private userAccessRequestsService: UserAccessRequestsService
  ) {
    // ngx-bootstrap date picker theme
    setTheme('bs4');
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    router.events
      .pipe(
        withLatestFrom(this.isHandset$),
        filter(([a, b]) => b && a instanceof NavigationEnd)
      )
      .subscribe((_) => this.sidenav.close());
  }

  ngAfterViewInit(): void {
    // Get the document root.
    const root: HTMLElement = document.documentElement;
    // Extract the styles from the document root.
    const styles: CSSStyleDeclaration = getComputedStyle(root);
    // Update the primary color with one from the styles.
    this.primaryColor = styles.getPropertyValue('--primary-color').trim();
  }

  /**
   * Resolves the left navigation visibility flags from the subscription plan and the user's RBAC
   * permissions. A menu entry shows when the user holds ANY permission for that feature, matching
   * the previous inline template checks exactly.
   *
   * @return {void}
   */
  private resolveNavPermissions(): void {
    const F = RBACService.RBAC_F;
    const A = RBACService.RBAC_A;
    const FEATURES = AppService.FEATURES;

    // Builds the permission list for every action of a feature.
    const anyAction = (feature: string): string[] => [
      RBACService.getPermission(feature, A.A_CREATE),
      RBACService.getPermission(feature, A.A_VIEW),
      RBACService.getPermission(feature, A.A_EDIT),
      RBACService.getPermission(feature, A.A_DELETE)
    ];

    // Does the active account offer account features at all (SC-301)?
    //
    // False on the user's own personal account, which is not a workspace: no sites, no employees, no
    // company content, one member. Everything in this navigation is account-level, so on a personal
    // account the whole menu collapses to Dashboard, which is ungated in the template and is where the
    // My Inductions widget lives.
    //
    // Stated explicitly only for the FREE entries (contractors, employees, tasks, clientSites,
    // clientPreQualifications). Every premium entry inherits the same rule through planCan(), which
    // returns false on a personal account, so repeating it there would be noise.
    //
    // Super admins keep full access, matching planCan() and EnsurePlanFeature on the API, so support
    // staff who switch into somebody's personal account can still work.
    const accountFeatures: boolean = this.app.user.is_sitesoft_admin
      || this.app.accountFeaturesAvailable();

    const canRegistersInspectionsAudits: boolean = this.rbac.userCanAccess([
      RBACService.getPermission(F.F_REGISTERS_INSPECTIONS_AUDITS, A.A_VIEW),
      RBACService.getPermission(F.F_REGISTERS_INSPECTIONS_AUDITS, A.A_EDIT),
      RBACService.getPermission(F.F_REGISTERS_INSPECTIONS_AUDITS, A.A_DELETE)
    ]);

    this.navCan = {
      sites: this.app.planCan(FEATURES.SITES) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_SITES, A.A_CREATE),
        RBACService.getPermission(F.F_SITES, A.A_VIEW)
      ]),
      safetyPlans: this.app.planCan(FEATURES.SAFETY_PLANS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_SAFETY_PLANS, A.A_VIEW)
      ]),
      contractors: accountFeatures && this.rbac.userCanAccess(anyAction(F.F_CONTRACTORS)),

      // Employees now also hosts the Access Requests tab, which is governed by its own permission. A
      // reviewer given only the approvals permission has to be able to reach the page, so the menu entry
      // shows for either. Without this they would hold a permission with no route to it.
      employees: accountFeatures && this.rbac.userCanAccess([
        ...anyAction(F.F_EMPLOYEES),
        RBACService.getPermission(F.F_EMPLOYEES_ACCESS_REQUESTS, A.A_VIEW),
        RBACService.getPermission(F.F_EMPLOYEES_ACCESS_REQUESTS, A.A_EDIT)
      ]),
      tasks: accountFeatures && this.rbac.userCanAccess(anyAction(F.F_TASKS)),
      accountInductions: this.app.planCan(FEATURES.INDUCTIONS)
        && this.rbac.userCanAccess(anyAction(F.F_ACCOUNT_INDUCTIONS)),
      forms: this.app.planCan(FEATURES.FORMS) && this.rbac.userCanAccess(anyAction(F.F_FORMS)),

      // Registers group: any of the register plan features plus any register permission.
      //
      // The asset entries (Asset Register, Asset Types, Asset Vendors) moved in from the former
      // dedicated Assets menu, so ASSETS and its three RBAC features are included here. Without that
      // a user whose only permissions are asset ones would lose the menu entirely, since the panel
      // that used to carry them no longer exists.
      registers: (
        this.app.planCan(FEATURES.INCIDENTS) ||
        this.app.planCan(FEATURES.SAFETY_OBSERVATIONS) ||
        this.app.planCan(FEATURES.HAZARDS) ||
        this.app.planCan(FEATURES.HAZARDOUS_SUBSTANCES) ||
        this.app.planCan(FEATURES.INSPECTIONS_AUDITS) ||
        this.app.planCan(FEATURES.TOOLBOX_TALKS) ||
        this.app.planCan(FEATURES.TOOLS) ||
        this.app.planCan(FEATURES.ASSETS)
      ) && this.rbac.userCanAccess([
        ...anyAction(F.F_REGISTERS_INCIDENTS),
        RBACService.getPermission(F.F_REGISTERS_SAFETY_OBSERVATIONS, A.A_CREATE),
        RBACService.getPermission(F.F_REGISTERS_SAFETY_OBSERVATIONS, A.A_VIEW),
        RBACService.getPermission(F.F_REGISTERS_HAZARDS_RISKS, A.A_CREATE),
        RBACService.getPermission(F.F_REGISTERS_HAZARDS_RISKS, A.A_VIEW),
        RBACService.getPermission(F.F_REGISTERS_HAZARDOUS_SUBSTANCES, A.A_CREATE),
        RBACService.getPermission(F.F_REGISTERS_HAZARDOUS_SUBSTANCES, A.A_VIEW),
        RBACService.getPermission(F.F_REGISTERS_INSPECTIONS_AUDITS, A.A_VIEW),
        RBACService.getPermission(F.F_REGISTERS_INSPECTIONS_AUDITS, A.A_EDIT),
        RBACService.getPermission(F.F_REGISTERS_INSPECTIONS_AUDITS, A.A_DELETE),
        ...anyAction(F.F_REGISTERS_TOOLBOX_TALKS),
        ...anyAction(F.F_REGISTERS_TOOLS),
        ...anyAction(F.F_REGISTERS_ASSETS),
        ...anyAction(F.F_REGISTERS_ASSET_TYPES),
        ...anyAction(F.F_REGISTERS_VENDORS)
      ]),
      registersIncidents: this.app.planCan(FEATURES.INCIDENTS)
        && this.rbac.userCanAccess(anyAction(F.F_REGISTERS_INCIDENTS)),
      registersSafetyObservations: this.app.planCan(FEATURES.SAFETY_OBSERVATIONS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_REGISTERS_SAFETY_OBSERVATIONS, A.A_CREATE),
        RBACService.getPermission(F.F_REGISTERS_SAFETY_OBSERVATIONS, A.A_VIEW)
      ]),
      registersHazards: this.app.planCan(FEATURES.HAZARDS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_REGISTERS_HAZARDS_RISKS, A.A_CREATE),
        RBACService.getPermission(F.F_REGISTERS_HAZARDS_RISKS, A.A_VIEW)
      ]),
      registersHazardousSubstances: this.app.planCan(FEATURES.HAZARDOUS_SUBSTANCES) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_REGISTERS_HAZARDOUS_SUBSTANCES, A.A_CREATE),
        RBACService.getPermission(F.F_REGISTERS_HAZARDOUS_SUBSTANCES, A.A_VIEW)
      ]),
      registersInspectionsAudits: this.app.planCan(FEATURES.INSPECTIONS_AUDITS) && canRegistersInspectionsAudits,
      registersToolboxTalks: this.app.planCan(FEATURES.TOOLBOX_TALKS)
        && this.rbac.userCanAccess(anyAction(F.F_REGISTERS_TOOLBOX_TALKS)),
      registersTools: this.app.planCan(FEATURES.TOOLS)
        && this.rbac.userCanAccess(anyAction(F.F_REGISTERS_TOOLS)),

      // Asset registers, now rendered inside the Registers group rather than a dedicated Assets menu.
      // The group flag above accounts for them. The site level Asset Register lives in the site
      // navigation, not here.
      registersAssets: this.app.planCan(FEATURES.ASSETS)
        && this.rbac.userCanAccess(anyAction(F.F_REGISTERS_ASSETS)),
      registersAssetTypes: this.app.planCan(FEATURES.ASSETS)
        && this.rbac.userCanAccess(anyAction(F.F_REGISTERS_ASSET_TYPES)),
      registersVendors: this.app.planCan(FEATURES.ASSETS)
        && this.rbac.userCanAccess(anyAction(F.F_REGISTERS_VENDORS)),

      // Templates group: any of the template plan features plus any template permission.
      templates: (
        this.app.planCan(FEATURES.TASK_ANALYSIS) ||
        this.app.planCan(FEATURES.DOCUMENTS) ||
        this.app.planCan(FEATURES.PRE_QUALIFICATIONS) ||
        this.app.planCan(FEATURES.INSPECTIONS_AUDITS) ||
        this.app.planCan(FEATURES.TOOLBOX_TALKS)
      ) && this.rbac.userCanAccess([
        ...anyAction(F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS),
        RBACService.getPermission(F.F_TEMPLATES_TA_JSA_SWMS, A.A_CREATE),
        RBACService.getPermission(F.F_TEMPLATES_TA_JSA_SWMS, A.A_VIEW),
        RBACService.getPermission(F.F_TEMPLATES_DOCUMENTS, A.A_CREATE),
        RBACService.getPermission(F.F_TEMPLATES_DOCUMENTS, A.A_VIEW),
        RBACService.getPermission(F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS, A.A_CREATE),
        RBACService.getPermission(F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS, A.A_VIEW),
        ...anyAction(F.F_TEMPLATES_INSPECTIONS_AUDITS),
        ...anyAction(F.F_TEMPLATES_TOOLBOX_TALKS)
      ]),
      templatesTaskAnalysis: this.app.planCan(FEATURES.TASK_ANALYSIS)
        && this.rbac.userCanAccess(anyAction(F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS)),
      templatesTaJsaSwms: this.app.planCan(FEATURES.TASK_ANALYSIS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_TEMPLATES_TA_JSA_SWMS, A.A_CREATE),
        RBACService.getPermission(F.F_TEMPLATES_TA_JSA_SWMS, A.A_VIEW)
      ]),
      templatesDocuments: this.app.planCan(FEATURES.DOCUMENTS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_TEMPLATES_DOCUMENTS, A.A_VIEW),
        RBACService.getPermission(F.F_TEMPLATES_DOCUMENTS, A.A_CREATE)
      ], false),
      templatesPreQualifications: this.app.planCan(FEATURES.PRE_QUALIFICATIONS)
        && this.rbac.userCanAccess(anyAction(F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS)),
      templatesInspectionsAudits: this.rbac.userCanAccess(anyAction(F.F_TEMPLATES_INSPECTIONS_AUDITS))
        && this.app.planCan(FEATURES.INSPECTIONS_AUDITS),
      templatesToolboxTalks: this.app.planCan(FEATURES.TOOLBOX_TALKS)
        && this.rbac.userCanAccess(anyAction(F.F_TEMPLATES_TOOLBOX_TALKS)),

      // Reports group.
      reports: (
        this.app.planCan(FEATURES.SITE_ACCESS) ||
        this.app.planCan(FEATURES.VISITORS)
      ) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_REPORTS_SITE_ACCESS, A.A_VIEW),
        RBACService.getPermission(F.F_REPORTS_VISITORS, A.A_VIEW)
      ]),
      reportsSiteAccess: this.app.planCan(FEATURES.SITE_ACCESS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_REPORTS_SITE_ACCESS, A.A_VIEW)
      ]),
      reportsVisitors: this.app.planCan(FEATURES.VISITORS) && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_REPORTS_VISITORS, A.A_VIEW)
      ]),

      messages: this.app.planCan(FEATURES.MESSAGES) && this.rbac.userCanAccess(anyAction(F.F_MESSAGES)),
      clientSites: accountFeatures && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_CLIENT_SITES, A.A_VIEW),
        RBACService.getPermission(F.F_CLIENT_SITES, A.A_EDIT),
        RBACService.getPermission(F.F_CLIENT_SITES, A.A_DELETE)
      ]),
      clientPreQualifications: accountFeatures && this.rbac.userCanAccess([
        RBACService.getPermission(F.F_CLIENT_PRE_QUALIFICATIONS, A.A_VIEW),
        RBACService.getPermission(F.F_CLIENT_PRE_QUALIFICATIONS, A.A_EDIT)
      ])
    };
  }

  async ngOnInit() {
    // Recompute the navigation flags whenever the user's permissions change. This component is
    // never destroyed, so resolving them only once here would leave the menu stale after sign in
    // (the session is restored asynchronously), account switching, or super admin quick access.
    this.permissionsSubscription = this.rbac.permissionsChanged$.subscribe((): void => {
      this.resolveNavPermissions();

      // The approvals badge and the pending banner both depend on the active account, so they are
      // refreshed on the same signal as the menu. Doing this only in ngOnInit would leave a badge from the
      // previous account visible after switching, and would miss sign in entirely because the session is
      // restored asynchronously.
      //
      // Flagged as background requests: the user did not trigger them, so a failure to reach the server
      // must not open a modal.
      this.accessRequestsService.refreshPendingCount(true);
      this.userAccessRequestsService.refresh(true);
    });

    this.pendingCountSubscription = this.accessRequestsService.pendingCount.subscribe(
      (count: number): void => {
        this.pendingAccessRequests = count;
      }
    );

    // Only a PENDING request raises the banner. A declined request is not shown here because a permanent
    // banner about a refusal would nag on every page; the Account Access entry in the account menu is the
    // route to that detail instead.
    this.ownRequestsSubscription = this.userAccessRequestsService.requests.subscribe(
      (requests: any[]): void => {
        const pendingRequests = (requests || []).filter(
          (row: any): boolean => row.approval_status === 'pending'
        );

        // Counted, not just found. A user can have several requests outstanding at once, and naming only
        // the first would tell them they are waiting on one company while quietly omitting the others.
        this.ownPendingCount = pendingRequests.length;
        this.ownPendingRequest = pendingRequests.length
          ? { id: pendingRequests[0].id, account_name: pendingRequests[0].account_name }
          : null;
      }
    );

    // Initialize Sentry
    const allowSentry = await this.sentry.allowInit();
    if (allowSentry) {
      this.sentry.onInit();
    }

    if (
      !this.app.isAuthenticated &&
      !(['/','/sign-in','/sign-up','/about','/contact-us','/noauth','/reset-password','/set-password','/verify'].some(path => location.pathname.includes(path)))
    ) {
      this.router.navigate(['sign-in'], {
        queryParams: {
          redirect: location.pathname,
          queryParams: location.search
        }
      });
    }

    // detect IE/Edge Legacy
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('MSIE ') > -1 || userAgent.indexOf('Trident/') > -1 || userAgent.indexOf('Edge/') > -1) {
      this.utils.showModal(
        'Internet Explorer/Edge Legacy - Not Fully Supported',
        'Internet Explorer is outdated and not fully supported by this web application. ' +
        'Perhaps try the <a href="https://www.google.com/chrome/" target="_blank">Google Chrome</a> (recommended), <a href="https://www.mozilla.org/en-US/firefox/" target="_blank">Firefox</a> or <a href="https://www.microsoft.com/en-nz/windows/microsoft-edge" target="_blank">Microsoft Edge</a> web browsers.'
      );
    }

    // Run code every 1 minutes
    this.minuteInterval = setInterval(() => {
      this.minuteIntervalFunctions();
    }, 60000);

    // Preloads external scripts.
    this.preloadExternalScripts();

    this.appcuesService.onInit();

    // Load the HubSpot live chat widget (only when a portal id is configured).
    this.hubspotService.onInit();

    this.registerTabVisibilityChange();
  }

  /**
   * Keeps this tab in step with the API when the user comes back to it.
   *
   * Purpose: something may have changed while the tab was in the background. The user may have switched
   * account (or signed in as someone else) in another tab, which swaps the shared AuthToken in
   * localStorage under this tab, or an administrator may have changed the user's role or the account's plan.
   * The tab then shows one account's data and menus while its requests run under another.
   *
   * This used to revalidate the token and call refreshPage() on EVERY visibilitychange. That destroyed the
   * routed component each time (losing unsaved forms and filters), bounced through '' where SignInComponent
   * runs its own auto sign-in and can race the way back, and cost an authentication round trip per focus.
   * Now:
   * - nothing happens unless the user is signed in and not on the sign-in screen;
   * - the API is asked at most once per TAB_SYNC_MIN_INTERVAL_MS, except when the stored token is no longer
   *   the one this tab last saw, which means another tab signed in or switched account and this tab must
   *   catch up immediately;
   * - the response is compared with the current state (user id, account id, subscription plan and a hash of
   *   the permission list) and the app state is only re-applied, and the route only reloaded, when
   *   something actually differs;
   * - a failed check is silent. It is a background courtesy, not a user action: a 401 is already routed to
   *   sign-in by the error interceptor, and a network blip should not open an error dialog on every focus.
   *   The request is flagged BACKGROUND_REQUEST so the interceptor stays quiet, and nothing here signs the
   *   user out, so a transport failure leaves the session and local storage alone. A failed check does not
   *   count against the throttle, so the next focus tries again;
   * - the check is not sent while offline, and waits RESUME_DELAY_MS after the tab becomes visible so the
   *   request does not straddle a network change. The exception is a token replaced by another tab, which
   *   is checked immediately because correctness outranks the settle delay.
   *
   * API cost: v2/api-token-validation is the full auth payload endpoint and the API offers no lighter
   * revalidation route, so the throttle above is what bounds the load (at most one call per minute per tab).
   *
   * @private
   */
  private deregisterTabVisibilityChange?: () => void;
  private static readonly TAB_SYNC_MIN_INTERVAL_MS: number = 60000;
  private lastTabSyncAt: number = 0;
  private lastTabSyncToken: string | null = null;
  private tabSyncInFlight: boolean = false;
  private tabSyncTimer?: ReturnType<typeof setTimeout>;

  private registerTabVisibilityChange(): void {
    // The app has just loaded, and loading validated the session, so the first check is a minute away.
    this.lastTabSyncAt = Date.now();
    this.lastTabSyncToken = this.storage.get('AuthToken') || null;

    const onVisibilityChange = (): void => {
      if ( document.visibilityState !== 'visible' ) {
        return;
      }

      // Nothing to sync for a signed out visitor, on the sign-in screen (which validates by itself), or
      // while a previous check is still running.
      if ( !this.app.isAuthenticated || this.tabSyncInFlight || this.isOnSignInRoute() ) {
        return;
      }

      const apiToken: string = this.storage.get('AuthToken');
      if ( !apiToken ) {
        return;
      }

      // Another tab replaced the token (sign in, account switch) since this tab last looked: catch up now.
      const tokenChangedElsewhere: boolean = apiToken !== this.lastTabSyncToken;
      const now: number = Date.now();
      if ( !tokenChangedElsewhere && now - this.lastTabSyncAt < AppComponent.TAB_SYNC_MIN_INTERVAL_MS ) {
        return;
      }

      // A delayed check is already scheduled, and there is no network to check with while offline.
      if ( this.tabSyncTimer || navigator.onLine === false ) {
        return;
      }
      const runTabSync = (apiToken: string): void => {
        // Remembered so a failed check can be undone: it must not use up the throttle window.
        const previousSyncAt: number = this.lastTabSyncAt;
        const previousToken: string | null = this.lastTabSyncToken;
        this.tabSyncInFlight = true;
        this.lastTabSyncAt = Date.now();
        this.lastTabSyncToken = apiToken;
        this.api.makeRequest('post', 'v2/api-token-validation', {
          api_token: apiToken
        }, {}, {}, false, true)
          .then((response): void => {
            if ( !response?.account || !response?.user ) {
              return;
            }

            // Same user, account, plan and permissions: leave the page, its forms and the services alone.
            if ( !this.authDetailsDiffer(response) ) {
              return;
            }

            // Captured before anything navigates. router.url keeps the query string and fragment.
            const currentUrl: string = this.router.url;

            // Update in place first. This emits permissionsChanged$, so the menus and every component that
            // resolves permission flags recompute without a reload.
            this.authService.updateAccountAuthDetails(response);
            this.lastTabSyncToken = this.storage.get('AuthToken') || null;

            // Then re-create the current route, because it was built for the previous account or
            // permissions: its data may belong to another account, and its guards need to run again.
            this.reloadCurrentRoute(currentUrl);
          })
          .catch((): void => {
            // Deliberately silent, see the method comment. Restore the throttle state so the next focus
            // tries again instead of waiting out the window, or forgetting a token change.
            this.lastTabSyncAt = previousSyncAt;
            this.lastTabSyncToken = previousToken;
          })
          .finally((): void => {
            this.tabSyncInFlight = false;
          });
      };
      if ( tokenChangedElsewhere ) {
        // Another tab switched account: catch up now, without the settle delay.
        runTabSync(apiToken);
        return;
      }
      // Wait a moment so the request does not straddle a network change as the tab wakes up.
      this.tabSyncTimer = setTimeout((): void => {
        this.tabSyncTimer = undefined;
        if (
          document.visibilityState !== 'visible'
          || navigator.onLine === false
          || !this.app.isAuthenticated
          || this.tabSyncInFlight
          || this.isOnSignInRoute()
        ) {
          return;
        }
        const token: string = this.storage.get('AuthToken');
        if ( token ) {
          runTabSync(token);
        }
      }, AppComponent.RESUME_DELAY_MS);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    this.deregisterTabVisibilityChange = (): void => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearTimeout(this.tabSyncTimer);
      this.tabSyncTimer = undefined;
    };
  }

  /**
   * Whether the router is on the sign-in screen. The root path renders it too, so both count.
   */
  private isOnSignInRoute(): boolean {
    const path: string = this.router.url.split(/[?#]/)[0];
    return path === '' || path === '/' || path.startsWith('/sign-in');
  }

  /**
   * Whether a validation response describes a different session context than the one this tab holds.
   * Permissions are compared as a sorted, de-duplicated list so ordering differences are not a change.
   */
  private authDetailsDiffer(response: any): boolean {
    const hash = (permissions: string[]): string => Array.from(new Set(permissions || [])).sort().join('|');

    return (
      response.user.id !== this.app.user.id ||
      response.account.id !== this.app.account.id ||
      !!response.user.is_sitesoft_admin !== !!this.app.user.is_sitesoft_admin ||
      response.account.subscription !== this.app.account.subscription ||
      hash(response.permissions) !== hash(this.rbac.permissions)
    );
  }

  /**
   * Re-creates the current route so it is rebuilt for the new account or permissions.
   *
   * Angular reuses a component when the URL does not change, so the route is first left for a neutral page
   * that has no behaviour of its own (PageNotFoundComponent, reached with skipLocationChange so the address
   * bar never changes) and then re-entered with the full URL. It must not go through '', which renders
   * SignInComponent and its auto sign-in. Passing the whole router.url string keeps the query string.
   */
  private reloadCurrentRoute(url: string): void {
    this.router.navigateByUrl('/not-found', { skipLocationChange: true })
      .then((): Promise<boolean> => this.router.navigateByUrl(url));
  }

  /**
   * Preloads external scripts required for the software. These scripts will load asynchronously.
   *
   * @return {void}
   */
  preloadExternalScripts(): void {
    // Load the Google charts library into the software.
    this.utils.loadExternalScript('google-charts', 'https://www.gstatic.com/charts/loader.js', () => {});
  }

  minuteIntervalFunctions() {
    if ( !this.app.isAuthenticated ) {
      return;
    }
    // Skip while offline, while the tab is hidden, or inside the settle window after the network or the
    // tab came back. A poll nobody can see, or one that straddles a network change, only fails.
    if ( navigator.onLine === false || document.visibilityState !== 'visible' || Date.now() < this.pollNotBefore ) {
      this.pollSkipped = true;
      return;
    }
    this.pollSkipped = false;
    this.userMessagesService.getMessagesMeta();
  }

  /**
   * The network came back, or the tab became visible again. Catch up on a poll that was skipped, after a
   * short delay so the first request does not straddle the network change.
   *
   * Only does so if a scheduled poll was actually skipped, so switching tabs does not add a poll per switch.
   */
  @HostListener('window:online')
  @HostListener('document:visibilitychange')
  onConnectivityResume(): void {
    if ( document.visibilityState !== 'visible' ) {
      return;
    }
    this.pollNotBefore = Date.now() + AppComponent.RESUME_DELAY_MS;
    if ( !this.pollSkipped ) {
      return;
    }
    clearTimeout(this.pollResumeTimer);
    this.pollResumeTimer = setTimeout((): void => {
      this.pollNotBefore = 0;
      this.minuteIntervalFunctions();
    }, AppComponent.RESUME_DELAY_MS);
  }

  ngOnDestroy() {
    this.mobileQuery.removeListener(this._mobileQueryListener);

    this.permissionsSubscription?.unsubscribe();
    this.pendingCountSubscription?.unsubscribe();
    this.ownRequestsSubscription?.unsubscribe();

    // Clear all intervals
    clearInterval(this.minuteInterval);
    clearTimeout(this.pollResumeTimer);

    // Unsubscribe from Google Analytics.
    this.googleAnalyticsService.destroy();

    this.appcuesService.onDestroy();

    this.deregisterTabVisibilityChange?.();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    // The side menu is only for signed in users, so keep it closed when signed out.
    if (event.target.innerWidth < 600 || !this.app.isAuthenticated) {
      this.sidenav.close();
    } else {
      this.sidenav.open();
    }
  }

  public readonly RBACService = RBACService;
  public readonly AppService = AppService;
}
